import { createUrl } from 'src/feature/common/utils/url';
import { BACKEND_URL } from 'src/constants';
import { createFilterParamsString, makeAsyncCall } from 'src/utils';
import {
  IAddOrUpdateSurveyCategoryData,
  IResourceQuery,
  ISurvey,
  IUser,
} from '../types';

const MAX_TAGS_COUNT = 9999999;

const index = (query: IResourceQuery) =>
  makeAsyncCall({
    url: createFilterParamsString(query, `${BACKEND_URL}surveys`),
    method: 'GET',
  });

const getCategories = (query: IResourceQuery) =>
  makeAsyncCall({
    url: createFilterParamsString(query, `${BACKEND_URL}survey-categories`),
    method: 'GET',
  });

const handleAddCategory = (data: IAddOrUpdateSurveyCategoryData) =>
  makeAsyncCall({
    url: `${BACKEND_URL}survey-categories`,
    method: 'POST',
    body: JSON.stringify(data),
  });

const handleUpdateCategory = (data: IAddOrUpdateSurveyCategoryData) =>
  makeAsyncCall({
    url: `${BACKEND_URL}survey-categories/${data.id}`,
    method: 'PUT',
    body: JSON.stringify(data),
  });

const handleDeleteCategory = (categoryId: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}survey-categories/${categoryId}`,
    method: 'DELETE',
  });

const handleSortSurveys = (data: {
  space_id: number;
  survey_category_id: number;
  survey_ids: number[];
}) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/sort`,
    method: 'PUT',
    body: JSON.stringify(data),
  });

const handleSortCategories = (data: {
  space_id: number;
  survey_category_ids: number[];
}) =>
  makeAsyncCall({
    url: `${BACKEND_URL}survey-categories/sort`,
    method: 'PUT',
    body: JSON.stringify(data),
  });

const store = (data: ISurvey) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys`,
    method: 'POST',
    body: JSON.stringify(data),
  });

const getSurvey = (id: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${id}`,
    method: 'GET',
  });

const getKeyFigures = (id: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${id}/key-figures`,
    method: 'GET',
  });

const handleSubmitSurvey = (data: any, surveyId) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/submit`,
    method: 'POST',
    body: JSON.stringify(data),
  });

const handleEditSurvey = (body, surveyId) => {
  body.append('_method', 'PUT');
  return makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}`,
    method: 'POST',
    body,
    headers: {
      'Content-Type': null,
    },
  });
};

const handleGetUsers = (surveyId, query) =>
  makeAsyncCall({
    url: createUrl(
      `surveys/${surveyId}/users`,
      query.filter,
      query.sort,
      query.per_page,
      query.page
    ),
    method: 'GET',
  });

const handleAddUser = (surveyId: number, data: IUser) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/users`,
    method: 'POST',
    body: JSON.stringify(data),
  });

const handleRemoveUser = (surveyId: number, userId: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/users/${userId}`,
    method: 'DELETE',
  });

const remove = surveyId =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}`,
    method: 'DELETE',
  });

const handleSenInvitation = (
  surveyId: number,
  surveyStageId: number,
  participantGroupIds: number[] | '*',
  force: boolean
) => {
  return makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/send-invitation-email`,
    method: 'POST',
    body: JSON.stringify({
      participant_group_ids: participantGroupIds,
      survey_stage_id: surveyStageId,
      force,
    }),
  });
};

const handleSendRecordingReminder = (surveyId: number, surveyStageId: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/send-recording-reminder`,
    method: 'POST',
    body: JSON.stringify({
      survey_stage_id: surveyStageId,
    }),
  });

const handleSendSubmittingReminder = (surveyId: number, surveyStageId: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/send-submitting-reminder`,
    method: 'POST',
    body: JSON.stringify({
      survey_stage_id: surveyStageId,
    }),
  });

const handleSendParticipantReactionReport = (surveyId: number, surveyStageId: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/send-participant-reaction-report`,
    method: 'POST',
    body: JSON.stringify({
      survey_stage_id: surveyStageId,
    }),
  });

const handleRemoveParticipantGroup = (surveyId: number, participantGroupIds: number[]) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/remove-participant-groups`,
    method: 'PUT',
    body: JSON.stringify({ participant_group_ids: participantGroupIds }),
  });

const handleDownloadParticipantGroupExportUrl = query =>
  makeAsyncCall({
    url: createUrl('participant-groups/export-url', query.filter),
    method: 'GET',
  });

const handlePublishSubmission = (surveyId: number, participantGroupId: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/publish-submission`,
    method: 'POST',
    body: JSON.stringify({
      participant_group_id: participantGroupId,
    }),
  });

const handleDispatchFindSimilarEvaluationGroups = (surveyId: number) =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/dispatch-search-for-similar-evaluation-groups`,
    method: 'POST',
  });

const updateSurveySortOrder = data =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/sort`,
    method: 'PUT',
    body: JSON.stringify(data),
  });

const archive = surveyId =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/archive`,
    method: 'PUT',
  });

const unArchive = surveyId =>
  makeAsyncCall({
    url: `${BACKEND_URL}surveys/${surveyId}/unarchive`,
    method: 'PUT',
  });

const handleDownloadGeneralReport = ({ filter }) =>
  makeAsyncCall({
    url: createUrl('surveys/general-report-download', filter),
    method: 'GET',
  });

// @deprecated. Use `EvaluationPageSettingsService.loadSettings`
const handleLoadSurveySettings = data => {
  return makeAsyncCall({
    url: createUrl('evaluation-page-settings', {}, '', 1, 1, undefined, data),
    method: 'GET',
  });
};

// @deprecated Use `EvaluationPageSettingsService.storeSettings`
const handleStoreSurveySettings = (data, surveySettingId) =>
  makeAsyncCall({
    url: `${BACKEND_URL}evaluation-page-settings${
      surveySettingId ? `/${surveySettingId}` : ''
    }`,
    method: surveySettingId ? 'PUT' : 'POST',
    body: JSON.stringify(data),
  });

const handleGetAllSurveyTags = spaceId =>
  makeAsyncCall({
    url: `${BACKEND_URL}survey-tags?filter[space_id]=${spaceId}&per_page=${MAX_TAGS_COUNT}`,
    method: 'GET',
  });

export const SurveyService = {
  index: (query: IResourceQuery) => index(query),
  getCategories: (query: IResourceQuery) => getCategories(query),
  handleAddCategory: (data: IAddOrUpdateSurveyCategoryData) => handleAddCategory(data),
  handleUpdateCategory: (data: IAddOrUpdateSurveyCategoryData) =>
    handleUpdateCategory(data),
  handleDeleteCategory: (categoryId: number) => handleDeleteCategory(categoryId),
  handleSortSurveys: data => handleSortSurveys(data),
  handleSortCategories: data => handleSortCategories(data),
  store: (data: ISurvey) => store(data),
  show: (id: number) => getSurvey(id),
  submit: (data: object, surveyId?: number) => handleSubmitSurvey(data, surveyId),
  editSurvey: (surveyData, surveyId) => handleEditSurvey(surveyData, surveyId),
  keyFigures: (id: number) => getKeyFigures(id),
  users: (surveyId, query) => handleGetUsers(surveyId, query),
  addUser: (surveyId: number, data: IUser) => handleAddUser(surveyId, data),
  removeUser: (surveyId: number, userId: number) => handleRemoveUser(surveyId, userId),
  remove: surveyId => remove(surveyId),
  sendInvitation: (
    surveyId: number,
    surveyStageId: number,
    participantGroupIds: number[] | '*',
    force: boolean
  ) => handleSenInvitation(surveyId, surveyStageId, participantGroupIds, force),
  sendRecordingReminder: (surveyId: number, surveyStageId: number) =>
    handleSendRecordingReminder(surveyId, surveyStageId),
  sendSubmittingReminder: (surveyId: number, surveyStageId: number) =>
    handleSendSubmittingReminder(surveyId, surveyStageId),
  sendParticipantReactionReport: (surveyId: number, surveyStageId: number) =>
    handleSendParticipantReactionReport(surveyId, surveyStageId),
  removeParticipantGroups: (surveyId: number, participantGroupIds: number[]) =>
    handleRemoveParticipantGroup(surveyId, participantGroupIds),
  downloadParticipantGroupExportUrl: query =>
    handleDownloadParticipantGroupExportUrl(query),
  publishSubmission: (surveyId: number, participantGroupId: number) =>
    handlePublishSubmission(surveyId, participantGroupId),
  dispatchFindSimilarEvaluationGroups: (surveyId: number) =>
    handleDispatchFindSimilarEvaluationGroups(surveyId),
  updateSurveySortOrder: data => updateSurveySortOrder(data),
  archive: (surveyId: number) => archive(surveyId),
  unarchive: (surveyId: number) => unArchive(surveyId),
  downloadGeneralReport: query => handleDownloadGeneralReport(query),
  loadSurveySettings: data => handleLoadSurveySettings(data),
  storeSurveySettings: (data, surveySettingId) =>
    handleStoreSurveySettings(data, surveySettingId),
  getAllSurveyTags: spaceId => handleGetAllSurveyTags(spaceId),
};
